import { useState } from "react"
import { Search, ExternalLink } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Badge } from "../components/ui/badge"
import { useLocale } from "../components/LocaleProvider"
import { useListings, useListingStats } from "../lib/queries"
import type { Listing } from "../types"
import type { TranslationKey } from "../i18n/translations"

const STATUS_VARIANT: Record<string, "info" | "success" | "warning" | "danger" | "neutral"> = {
  NEW: "info",
  MATCHED: "warning",
  PROCESSED: "success",
  SKIPPED: "neutral",
}

const STATUS_KEY: Record<string, string> = {
  NEW: "listing_status_new",
  MATCHED: "listing_status_matched",
  PROCESSED: "listing_status_processed",
  SKIPPED: "listing_status_skipped",
}

function Listings() {
  const { t } = useLocale()
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [sourceFilter, setSourceFilter] = useState<string>("")

  const { data, isLoading } = useListings({
    status: statusFilter || undefined,
    sourceName: sourceFilter || undefined,
  })
  const { data: stats } = useListingStats()

  const items: Listing[] = data?.items ?? []
  const filtered = items.filter((row) =>
    Object.values(row).some((v) => String(v).toLowerCase().includes(search.toLowerCase())),
  )

  const sources = Array.from(new Set(items.map((i) => i.sourceName))).sort()

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: "Total", value: stats.total, color: "text-foreground" },
            { label: t("listing_status_new"), value: stats.new, color: "text-blue-600" },
            { label: t("listing_status_matched"), value: stats.matched, color: "text-yellow-600" },
            {
              label: t("listing_status_processed"),
              value: stats.processed,
              color: "text-green-600",
            },
            {
              label: t("listing_status_skipped"),
              value: stats.skipped,
              color: "text-muted-foreground",
            },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">{s.label}</div>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search_records")}
            className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">{t("filter_all_statuses")}</option>
          <option value="NEW">{t("listing_status_new")}</option>
          <option value="MATCHED">{t("listing_status_matched")}</option>
          <option value="PROCESSED">{t("listing_status_processed")}</option>
          <option value="SKIPPED">{t("listing_status_skipped")}</option>
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">{t("filter_all_sources")}</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{t("th_id")}</TableHead>
              <TableHead>{t("th_full_name")}</TableHead>
              <TableHead>{t("th_source")}</TableHead>
              <TableHead>{t("th_price")}</TableHead>
              <TableHead>{t("th_rooms")}</TableHead>
              <TableHead>{t("th_sqft")}</TableHead>
              <TableHead>{t("th_location")}</TableHead>
              <TableHead>{t("th_matches")}</TableHead>
              <TableHead>{t("th_listing_status")}</TableHead>
              <TableHead>{t("th_application_link")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-xs text-muted-foreground">{row.id}</TableCell>
                <TableCell className="max-w-[200px] truncate" title={row.title}>
                  {row.title || "—"}
                </TableCell>
                <TableCell>
                  <Badge variant="neutral">{row.sourceName}</Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {row.totalRentGross != null
                    ? `${row.totalRentGross} €`
                    : row.totalRent != null
                      ? `${row.totalRent} €`
                      : "—"}
                </TableCell>
                <TableCell className="font-mono text-xs">{row.rooms ?? "—"}</TableCell>
                <TableCell className="font-mono text-xs">
                  {row.size != null ? `${row.size} m²` : "—"}
                </TableCell>
                <TableCell className="text-xs">{row.district || row.zipCode || "—"}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(row.matches || []).slice(0, 2).map((m) => (
                      <Badge
                        key={m.id}
                        variant={
                          m.tier === "excellent"
                            ? "success"
                            : m.tier === "good"
                              ? "info"
                              : "neutral"
                        }
                        className="text-xs"
                      >
                        {m.profile?.name} ({m.tier})
                      </Badge>
                    ))}
                    {(row.matches || []).length > 2 && (
                      <Badge variant="neutral" className="text-xs">
                        +{row.matches!.length - 2}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANT[row.status] || "neutral"}>
                    {t((STATUS_KEY[row.status] || row.status) as TranslationKey)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {row.applicationLink ? (
                    <a
                      href={row.applicationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t("btn_open")}
                    </a>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default Listings
